/* Wild Tenant — iWhisper grooming kit lander
   Gallery, kit selector, single-date delivery countdown, customer quote band,
   FAQ accordion, customer video rail, review rail, sticky buy bar, kit cart drawer. */
(() => {
  const init = (root) => {
    /* ---------------- Data rendered by Liquid ---------------- */
    let data = { connected: false, cur: 'GBP', localeTag: 'en-GB', kits: {}, s: {} };
    try {
      const raw = root.querySelector('[data-iw-i18n]');
      if (raw) data = Object.assign(data, JSON.parse(raw.textContent));
    } catch (error) { /* keep defaults */ }
    // Shopify HTML-escapes translations, and the drawer writes these strings as plain text.
    const decodeEntities = (value) => {
      if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
      const box = document.createElement('textarea');
      box.innerHTML = value;
      return box.value;
    };
    const S = data.s || {};
    Object.keys(S).forEach((key) => { S[key] = decodeEntities(S[key]); });
    Object.values(data.kits || {}).forEach((kit) => {
      ['label', 'saved', 'title', 'sub'].forEach((field) => { kit[field] = decodeEntities(kit[field]); });
    });
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fitters = [];

    /* ---------------- Gallery ---------------- */
    const track = root.querySelector('[data-gallery-track]');
    const slides = track ? [...track.querySelectorAll('[data-gallery-slide]')] : [];
    const thumbs = [...root.querySelectorAll('[data-gallery-tab]')];
    const prevBtns = [...root.querySelectorAll('[data-gallery-prev]')];
    const nextBtns = [...root.querySelectorAll('[data-gallery-next]')];
    const playBtn = root.querySelector('[data-gallery-play]');
    let index = 0;
    let userPaused = false;
    let galleryVisible = true;

    const videoOf = (i) => (slides[i] ? slides[i].querySelector('video') : null);
    const setPlayIcon = (playing) => {
      if (!playBtn) return;
      playBtn.classList.toggle('is-playing', playing);
      playBtn.setAttribute('aria-label', playing ? (playBtn.dataset.labelPause || 'Pause video') : (playBtn.dataset.labelPlay || 'Play video'));
    };
    const pauseGalleryVideos = () => slides.forEach((slide) => { const v = slide.querySelector('video'); if (v) v.pause(); });
    const syncVideo = () => {
      const active = videoOf(index);
      slides.forEach((slide, i) => { const v = slide.querySelector('video'); if (v && i !== index) v.pause(); });
      if (!playBtn) return;
      playBtn.hidden = !active;
      if (!active) return;
      if (galleryVisible && !userPaused) {
        active.play().then(() => setPlayIcon(true)).catch(() => setPlayIcon(false));
      } else {
        active.pause();
        setPlayIcon(false);
      }
    };
    const setIndex = (next, scroll = true) => {
      if (!slides.length) return;
      index = (next + slides.length) % slides.length;
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
      });
      if (scroll && track) track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
      const activeThumb = thumbs[index];
      if (activeThumb && activeThumb.parentElement) {
        const rail = activeThumb.parentElement;
        const target = activeThumb.offsetLeft - (rail.clientWidth - activeThumb.clientWidth) / 2;
        rail.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
      syncVideo();
    };
    if (track && slides.length) {
      let raf = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== index) setIndex(i, false);
        });
      }, { passive: true });
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => setIndex(i)));
      prevBtns.forEach((btn) => btn.addEventListener('click', () => setIndex(index - 1)));
      nextBtns.forEach((btn) => btn.addEventListener('click', () => setIndex(index + 1)));
      // Tap zones: left 42.5% = previous, right 42.5% = next. Swipes never fire a click.
      track.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        const rect = track.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = (event.clientX - rect.left) / rect.width;
        if (ratio < 0.425) setIndex(index - 1);
        else if (ratio > 0.575) setIndex(index + 1);
      });
      if (playBtn) {
        playBtn.addEventListener('click', () => {
          const v = videoOf(index);
          if (!v) return;
          if (v.paused) { userPaused = false; v.play().then(() => setPlayIcon(true)).catch(() => {}); }
          else { userPaused = true; v.pause(); setPlayIcon(false); }
        });
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            galleryVisible = entry.isIntersecting;
            if (!galleryVisible) pauseGalleryVideos();
            else syncVideo();
          });
        }, { threshold: 0.2 }).observe(track);
      }
      let resizeTimer = 0;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => track.scrollTo({ left: index * track.clientWidth }), 120);
      });
      setIndex(0, false);
    }

    /* ---------------- Keep one-line rows on one line ---------------- */
    // The strip scales its type down, never below 10px. If it still can't fit, the word "reviews"
    // drops first, then (only on the narrowest phones) the stars.
    const fitLine = (el) => {
      el.classList.remove('is-tight', 'is-tighter');
      if (!el.clientWidth) return;
      const overflows = () => el.scrollWidth > el.clientWidth;
      const shrink = () => {
        el.style.fontSize = '';
        let size = parseFloat(window.getComputedStyle(el).fontSize) || 13;
        while (overflows() && size > 10) {
          size -= 0.25;
          el.style.fontSize = `${size}px`;
        }
        return !overflows();
      };
      if (shrink()) return;
      el.classList.add('is-tight');
      if (shrink()) return;
      el.classList.add('is-tighter');
      shrink();
    };
    root.querySelectorAll('[data-fit-line]').forEach((el) => fitters.push(() => fitLine(el)));
    // Bullet lists: every line stays on one row, and all bullets share the same size.
    const fitList = (list) => {
      list.style.fontSize = '';
      const items = [...list.children];
      if (!items.length || !list.clientWidth) return;
      const overflows = () => items.some((li) => li.scrollWidth > li.clientWidth);
      let size = parseFloat(window.getComputedStyle(list).fontSize) || 15;
      while (overflows() && size > 11) {
        size -= 0.25;
        list.style.fontSize = `${size}px`;
      }
    };
    root.querySelectorAll('[data-fit-list]').forEach((el) => fitters.push(() => fitList(el)));

    /* ---------------- Kit selector ---------------- */
    const kitCards = [...root.querySelectorAll('[data-kit]')];
    let stickyHook = () => {};
    let selectedKit = 'complete';
    const syncKits = () => {
      kitCards.forEach((card) => {
        const active = card.dataset.kit === selectedKit;
        card.classList.toggle('is-selected', active);
        card.setAttribute('aria-checked', String(active));
      });
      stickyHook();
    };
    kitCards.forEach((card) => {
      card.addEventListener('click', () => { selectedKit = card.dataset.kit; syncKits(); });
      card.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target === card) {
          event.preventDefault();
          selectedKit = card.dataset.kit;
          syncKits();
        }
      });
    });

    /* ---------------- Delivery countdown ---------------- */
    const initDelivery = (delivery) => {
      const CUTOFF_HOUR = Number(delivery.dataset.cutoffHour || 17);
      const MIN_DAYS = Number(delivery.dataset.minDays || 10);
      const MAX_DAYS = Number(delivery.dataset.maxDays || MIN_DAYS);
      const WORK_DAYS = (delivery.dataset.workDays || '1,2,3,4,5,6').split(',').map((d) => Number(d.trim()));
      const useUkHolidays = (delivery.dataset.holidays || '') === 'GB';
      let localeTag = delivery.dataset.locale || data.localeTag || 'en-GB';
      // English outside the US/Canada uses British date order and spelling (Thu 7 Aug).
      if (/^en-/i.test(localeTag) && !/^en-(US|CA)/i.test(localeTag)) localeTag = 'en-GB';
      const DAY = 86400000;
      const utcDate = (y, m, d) => new Date(Date.UTC(y, m, d));
      const addDays = (d, n) => new Date(d.getTime() + n * DAY);
      const key = (d) => d.toISOString().slice(0, 10);
      const easterSunday = (y) => {
        const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
        const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
        return utcDate(y, month - 1, day);
      };
      const firstMonday = (y, m) => { let d = utcDate(y, m, 1); while (d.getUTCDay() !== 1) d = addDays(d, 1); return d; };
      const lastMonday = (y, m) => { let d = utcDate(y, m + 1, 0); while (d.getUTCDay() !== 1) d = addDays(d, -1); return d; };
      const holidayCache = {};
      const bankHolidays = (y) => {
        if (holidayCache[y]) return holidayCache[y];
        const set = new Set();
        const easter = easterSunday(y);
        [addDays(easter, -2), addDays(easter, 1), firstMonday(y, 4), lastMonday(y, 4), lastMonday(y, 7)].forEach((d) => set.add(key(d)));
        [utcDate(y, 0, 1), utcDate(y, 11, 25), utcDate(y, 11, 26)].forEach((d) => {
          let s = d;
          while (s.getUTCDay() === 0 || s.getUTCDay() === 6 || set.has(key(s))) s = addDays(s, 1);
          set.add(key(s));
        });
        holidayCache[y] = set;
        return set;
      };
      const isBusinessDay = (d) => WORK_DAYS.includes(d.getUTCDay()) && (!useUkHolidays || !bankHolidays(d.getUTCFullYear()).has(key(d)));
      const nextBusinessDay = (d) => { let n = addDays(d, 1); while (!isBusinessDay(n)) n = addDays(n, 1); return n; };
      const addBusinessDays = (d, n) => { let cur = d; for (let i = 0; i < n; i++) cur = nextBusinessDay(cur); return cur; };
      // Orders are processed on UK time, so the cut-off is always London time.
      const londonNow = () => {
        const parts = {};
        new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
          .formatToParts(new Date()).forEach((p) => { if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10); });
        return { today: utcDate(parts.year, parts.month - 1, parts.day), minutes: parts.hour * 60 + parts.minute };
      };
      const makeFormatter = (options) => {
        try { return new Intl.DateTimeFormat(localeTag, options); } catch (error) { return new Intl.DateTimeFormat('en-GB', options); }
      };
      const fmt = makeFormatter({ weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
      const fmtDay = makeFormatter({ weekday: 'short', day: 'numeric', timeZone: 'UTC' });
      const fmtShort = makeFormatter({ day: 'numeric', month: 'short', timeZone: 'UTC' });
      const fmtShortDay = makeFormatter({ day: 'numeric', timeZone: 'UTC' });
      const monthFirst = /^en-(US|CA)/i.test(localeTag);
      const unit = (n, u) => (u && u.length > 1 ? `${n} ${u}` : `${n}${u || ''}`);
      const textEl = delivery.querySelector('[data-delivery-text]');
      const template = S.timer || 'Order within [countdown] → FREE delivery by [date]';
      // Keeps the line on one row at its normal size: if the dates don't fit, the weekdays drop;
      // only if that still overflows does it wrap at the arrow. The type is never shrunk.
      let compact = false;
      let draw = () => {};
      const fit = () => {
        delivery.classList.remove('wt-delivery--wrap');
        delivery.style.fontSize = '';
        compact = false;
        draw();
        if (!delivery.clientWidth) return;
        const overflows = () => delivery.scrollWidth > delivery.clientWidth + 1;
        if (!overflows()) return;
        compact = true;
        draw();
        if (overflows()) delivery.classList.add('wt-delivery--wrap');
      };
      const render = () => {
        const { today, minutes } = londonNow();
        const beforeCutoff = minutes < CUTOFF_HOUR * 60;
        const dispatch = isBusinessDay(today) && beforeCutoff ? today : nextBusinessDay(today);
        const daysUntilCutoff = Math.round((dispatch.getTime() - today.getTime()) / DAY);
        const minutesLeft = daysUntilCutoff * 1440 + CUTOFF_HOUR * 60 - minutes;
        const hours = Math.floor(minutesLeft / 60);
        const mins = minutesLeft % 60;
        const start = addBusinessDays(dispatch, MIN_DAYS);
        const end = addBusinessDays(dispatch, MAX_DAYS);
        const countdown = hours > 0 ? `${unit(hours, S.h || 'h')} ${unit(mins, S.m || 'm')}` : unit(mins, S.m || 'm');
        const sameMonth = start.getUTCMonth() === end.getUTCMonth();
        const windowText = (monthFirst || !sameMonth) ? `${fmt.format(start)} – ${fmt.format(end)}` : `${fmtDay.format(start)} – ${fmt.format(end)}`;
        const shortWindow = (monthFirst || !sameMonth) ? `${fmtShort.format(start)} – ${fmtShort.format(end)}` : `${fmtShortDay.format(start)}–${fmtShort.format(end)}`;
        const full = { '[countdown]': countdown, '[date]': fmt.format(end), '[window]': windowText };
        const short = { '[countdown]': countdown, '[date]': fmtShort.format(end), '[window]': shortWindow };
        draw = () => {
          const values = compact ? short : full;
          if (!textEl) return;
          textEl.textContent = '';
          const fill = (container, text) => {
            text.split(/(\[countdown\]|\[date\]|\[window\])/).forEach((part) => {
              if (values[part] !== undefined) {
                const b = document.createElement('b');
                b.textContent = values[part];
                b.setAttribute(part === '[countdown]' ? 'data-delivery-countdown' : 'data-delivery-window', '');
                container.appendChild(b);
              } else if (part) {
                container.appendChild(document.createTextNode(part));
              }
            });
          };
          // Two unbreakable halves split at the arrow, so a forced wrap only ever breaks there.
          const arrow = template.indexOf('→');
          const halves = arrow > -1 ? [template.slice(0, arrow + 1).trimEnd(), template.slice(arrow + 1).trimStart()] : [template];
          halves.forEach((half, i) => {
            if (i > 0) textEl.appendChild(document.createTextNode(' '));
            const span = document.createElement('span');
            span.className = 'wt-delivery__part';
            fill(span, half);
            textEl.appendChild(span);
          });
        };
        delivery.hidden = false;
        fit();
      };
      render();
      setInterval(render, 30000);
      fitters.push(fit);
    };
    root.querySelectorAll('[data-delivery]').forEach(initDelivery);
    const refit = () => fitters.forEach((fn) => fn());
    let refitTimer = 0;
    window.addEventListener('resize', () => { clearTimeout(refitTimer); refitTimer = setTimeout(refit, 150); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit).catch(() => {});
    window.addEventListener('load', refit);
    refit();

    /* ---------------- Customer quote band ---------------- */
    const qTrack = root.querySelector('[data-quotes-track]');
    const qDots = [...root.querySelectorAll('[data-quotes-dot]')];
    if (qTrack && qDots.length) {
      const qSlides = [...qTrack.querySelectorAll('[data-quote]')];
      let qIndex = 0;
      let qTouched = false;
      let qVisible = false;
      const paintDots = () => qDots.forEach((dot, i) => {
        if (i === qIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const goQuote = (i) => {
        qIndex = (i + qSlides.length) % qSlides.length;
        qTrack.scrollTo({ left: qIndex * qTrack.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        paintDots();
      };
      let qRaf = 0;
      qTrack.addEventListener('scroll', () => {
        cancelAnimationFrame(qRaf);
        qRaf = requestAnimationFrame(() => {
          const i = Math.round(qTrack.scrollLeft / Math.max(1, qTrack.clientWidth));
          if (i !== qIndex) { qIndex = i; paintDots(); }
        });
      }, { passive: true });
      ['pointerdown', 'touchstart', 'wheel'].forEach((type) => qTrack.addEventListener(type, () => { qTouched = true; }, { passive: true }));
      qDots.forEach((dot, i) => dot.addEventListener('click', () => { qTouched = true; goQuote(i); }));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => entries.forEach((entry) => { qVisible = entry.isIntersecting; }), { threshold: 0.6 }).observe(qTrack);
      }
      // Rotates on its own until the visitor touches it.
      if (!reduceMotion) setInterval(() => { if (qVisible && !qTouched && !document.hidden) goQuote(qIndex + 1); }, 6000);
    }

    /* ---------------- FAQ accordion: one answer open at a time ---------------- */
    const faqs = [...root.querySelectorAll('.iw-faq details')];
    faqs.forEach((item) => item.addEventListener('toggle', () => {
      if (!item.open) return;
      faqs.forEach((other) => { if (other !== item && other.open) other.open = false; });
    }));

    /* ---------------- Customer video rail ----------------
       One video plays at a time. It starts just before the rail scrolls into view,
       swiping to another card pauses the previous one, scrolling past the section pauses
       everything, and scrolling back resumes the card the visitor was on. */
    const ugcRail = root.querySelector('[data-ugc-rail]');
    if (ugcRail) {
      const cards = [...ugcRail.querySelectorAll('[data-ugc-card]')];
      // Posters load only when the rail gets close, so they don't compete with the first screen.
      const posterVids = [...ugcRail.querySelectorAll('video[data-poster]')];
      let postersDone = false;
      const loadPosters = () => {
        if (postersDone) return;
        postersDone = true;
        posterVids.forEach((v) => { if (!v.getAttribute('poster')) v.setAttribute('poster', v.dataset.poster); });
      };
      if ('IntersectionObserver' in window) {
        const posterObserver = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) { loadPosters(); posterObserver.disconnect(); }
        }, { rootMargin: '0px 0px 300px 0px' });
        posterObserver.observe(ugcRail);
        // Otherwise fetch them shortly after the page has loaded, so they're ready before anyone scrolls there.
        const later = () => window.setTimeout(loadPosters, 2500);
        if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
      } else {
        loadPosters();
      }
      const vids = cards.map((card) => card.querySelector('video'));
      let active = 0;
      let railInView = false;
      const warm = (v) => {
        if (!v || v.dataset.warm) return;
        v.dataset.warm = '1';
        v.preload = 'auto';
        if (v.readyState === 0) v.load();
      };
      const playActive = () => {
        vids.forEach((v, i) => { if (i !== active && v && !v.paused) v.pause(); });
        const v = vids[active];
        if (!v || !railInView || document.hidden || reduceMotion) return;
        warm(v);
        const attempt = v.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
        warm(vids[active + 1]);
      };
      const setActive = (i) => {
        if (i < 0 || i >= cards.length || i === active) return;
        if (vids[active]) vids[active].pause();
        active = i;
        cards.forEach((card, k) => card.classList.toggle('is-active', k === active));
        playActive();
      };
      const visibleShare = (card, railRect) => {
        const r = card.getBoundingClientRect();
        const overlap = Math.min(r.right, railRect.right) - Math.max(r.left, railRect.left);
        return r.width ? Math.max(0, overlap) / r.width : 0;
      };
      const pickFromScroll = () => {
        const railRect = ugcRail.getBoundingClientRect();
        // Keep the current card while it is mostly on screen (desktop shows several at once).
        if (visibleShare(cards[active], railRect) >= 0.6) return;
        const next = cards.findIndex((card) => visibleShare(card, railRect) >= 0.6);
        if (next > -1) setActive(next);
      };
      let ugcRaf = 0;
      ugcRail.addEventListener('scroll', () => {
        cancelAnimationFrame(ugcRaf);
        ugcRaf = requestAnimationFrame(pickFromScroll);
      }, { passive: true });
      const bringIntoView = (i) => {
        const card = cards[i];
        if (!card) return;
        const pad = parseFloat(window.getComputedStyle(ugcRail).scrollPaddingLeft) || 0;
        const left = card.offsetLeft - ugcRail.offsetLeft - pad;
        const railRect = ugcRail.getBoundingClientRect();
        if (visibleShare(card, railRect) < 0.98) ugcRail.scrollTo({ left: Math.max(0, left), behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      cards.forEach((card, i) => card.addEventListener('click', () => {
        if (i === active) {
          // A tap on the playing card is the gesture some browsers need before they allow playback.
          const v = vids[i];
          if (v && v.paused) { railInView = true; v.play().catch(() => {}); }
          return;
        }
        setActive(i);
        bringIntoView(i);
      }));
      const prev = root.querySelector('[data-ugc-prev]');
      const next = root.querySelector('[data-ugc-next]');
      if (prev) prev.addEventListener('click', () => { const i = Math.max(0, active - 1); setActive(i); bringIntoView(i); });
      if (next) next.addEventListener('click', () => { const i = Math.min(cards.length - 1, active + 1); setActive(i); bringIntoView(i); });
      if (cards[0]) cards[0].classList.add('is-active');
      if ('IntersectionObserver' in window) {
        // Start buffering the first clip well before it arrives...
        new IntersectionObserver((entries) => entries.forEach((entry) => { if (entry.isIntersecting) warm(vids[active]); }), { rootMargin: '0px 0px 900px 0px' }).observe(ugcRail);
        // ...and start playing just before it is on screen. Leaving the viewport pauses it.
        new IntersectionObserver((entries) => entries.forEach((entry) => {
          railInView = entry.isIntersecting;
          if (railInView) playActive();
          else vids.forEach((v) => { if (v && !v.paused) v.pause(); });
        }), { rootMargin: '0px 0px 220px 0px', threshold: 0 }).observe(ugcRail);
      } else {
        railInView = true;
        playActive();
      }
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) vids.forEach((v) => { if (v && !v.paused) v.pause(); });
        else playActive();
      });
    }

    /* ---------------- Review rail arrows ---------------- */
    const reviewRail = root.querySelector('[data-review-rail]');
    if (reviewRail) {
      const step = () => { const card = reviewRail.querySelector('.wt-review-card'); return card ? card.getBoundingClientRect().width + 16 : reviewRail.clientWidth * 0.8; };
      const rp = root.querySelector('[data-reviews-prev]');
      const rn = root.querySelector('[data-reviews-next]');
      if (rp) rp.addEventListener('click', () => reviewRail.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (rn) rn.addEventListener('click', () => reviewRail.scrollBy({ left: step(), behavior: 'smooth' }));
    }

    /* ---------------- Sticky buy bar ---------------- */
    const cartDrawer = root.querySelector('[data-cart-drawer]');
    const cartAtc = root.querySelector('[data-atc]');
    const sticky = root.querySelector('[data-sticky-atc]');
    let stickyAllowed = false;
    let cartKit = null;
    let showCart = null;
    const updateSticky = () => {
      if (!sticky) return;
      const drawerOpen = cartDrawer && !cartDrawer.hidden;
      const show = stickyAllowed && !drawerOpen;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
    };
    if (sticky && cartAtc && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          stickyAllowed = !entry.isIntersecting;
          updateSticky();
        });
      }, { threshold: 0 }).observe(cartAtc);
      const cta = sticky.querySelector('[data-sticky-cta]');
      if (cta) {
        // Once the chosen kit is in the cart, the bar opens the cart drawer instead of scrolling back to the kits.
        const labelAdd = cta.textContent;
        const labelCheckout = cta.dataset.labelCheckout || labelAdd;
        stickyHook = () => {
          const ready = cartKit !== null && cartKit === selectedKit;
          cta.textContent = ready ? labelCheckout : labelAdd;
          cta.classList.toggle('is-checkout', ready);
        };
        cta.addEventListener('click', (event) => {
          event.preventDefault();
          if (cartKit !== null && cartKit === selectedKit && showCart) { showCart(); return; }
          const target = root.querySelector('[data-kits]');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    /* ---------------- Cart drawer ---------------- */
    if (!cartDrawer || !cartAtc) { syncKits(); return; }

    const cartCloseButtons = [...root.querySelectorAll('[data-cart-close]')];
    const cartCheckout = root.querySelector('[data-cart-checkout]');
    const cartStatus = root.querySelector('[data-cart-status]');
    const cartScroll = root.querySelector('[data-cart-scroll]');
    const cartImage = root.querySelector('[data-cart-image]');
    const includedEl = root.querySelector('[data-cart-included]');
    const upgradeEl = root.querySelector('[data-cart-upgrade]');
    const upgradeButton = root.querySelector('[data-cart-upgrade-button]');
    const cartRoot = (root.dataset.rootUrl || '/').replace(/\/?$/, '/');
    const cartPath = (path) => `${cartRoot}${path.replace(/^\/+/, '')}`;
    showCart = () => { renderDrawer(); openDrawer(); };
    const group = 'iwhisper';
    let cartBusy = false;
    let lastFocus = null;

    const setStatus = (text, isError = false) => {
      cartStatus.textContent = text || '';
      cartStatus.hidden = !text;
      cartStatus.classList.toggle('is-error', isError);
    };
    const setBusy = (busy) => {
      cartBusy = busy;
      cartDrawer.classList.toggle('is-busy', busy);
      cartAtc.disabled = busy;
      if (upgradeButton) upgradeButton.disabled = busy;
      if (busy) cartCheckout.disabled = true;
    };
    const renderDrawer = () => {
      const kit = (data.kits || {})[selectedKit] || {};
      root.querySelector('[data-cart-title]').textContent = kit.title || '';
      root.querySelector('[data-cart-price]').textContent = kit.label || '';
      root.querySelector('[data-cart-saved]').textContent = kit.saved || '';
      const wasEl = root.querySelector('[data-cart-was]');
      if (wasEl) wasEl.textContent = kit.was || '';
      root.querySelector('[data-cart-sub]').textContent = kit.sub || '';
      if (cartImage) {
        const src = selectedKit === 'basic' ? cartImage.dataset.srcBasic : cartImage.dataset.srcComplete;
        if (src && cartImage.getAttribute('src') !== src) cartImage.setAttribute('src', src);
      }
      if (includedEl) includedEl.hidden = selectedKit !== 'complete';
      if (upgradeEl) upgradeEl.hidden = selectedKit !== 'basic';
      syncKits();
    };
    const openDrawer = () => {
      if (!cartDrawer.hidden) return;
      lastFocus = document.activeElement;
      cartDrawer.hidden = false;
      if (cartScroll) cartScroll.scrollTop = 0;
      document.documentElement.style.overflow = 'hidden';
      // Horizon scrolls .page-wrapper instead of the document on wide screens, so lock that too.
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) wrapper.style.overflow = 'hidden';
      updateSticky();
      window.requestAnimationFrame(() => {
        cartDrawer.classList.add('is-open');
        refit();
        root.querySelector('[data-cart-close]').focus();
      });
    };
    const closeDrawer = () => {
      cartDrawer.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper) wrapper.style.overflow = '';
      window.setTimeout(() => {
        cartDrawer.hidden = true;
        updateSticky();
        if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      }, 260);
    };
    const cartRequest = async (path, options = {}) => {
      const response = await fetch(cartPath(path), {
        ...options,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.description || body.message || 'Shopify could not update the cart.');
      return body;
    };
    // Checkout only unlocks when the cart holds exactly the chosen kit at the advertised price.
    const verifyKit = (cart, kit) => {
      if (!cart || !kit || cart.items.length !== 1) return false;
      const line = cart.items[0];
      const currencyMatches = !cart.currency || cart.currency === data.cur;
      return line.variant_id === kit.id && line.quantity === 1 && currencyMatches && cart.total_price === kit.price;
    };
    const replaceKit = async (nextKit = selectedKit) => {
      if (cartBusy) return;
      selectedKit = nextKit === 'basic' ? 'basic' : 'complete';
      renderDrawer();
      const kit = (data.kits || {})[selectedKit];
      if (!data.connected || !kit || !kit.id) {
        cartCheckout.disabled = true;
        setStatus(S.preview || 'Preview only: checkout is switched off.');
        return;
      }
      setBusy(true);
      setStatus(S.updating || 'Updating your kit…');
      try {
        const current = await cartRequest('cart.js');
        const updates = current.items.reduce((result, item) => { result[item.key] = 0; return result; }, {});
        if (Object.keys(updates).length) await cartRequest('cart/update.js', { method: 'POST', body: JSON.stringify({ updates }) });
        // A visible property is the only way to show the saving inside Shopify checkout on this plan.
        const properties = { _wt_group: group, _wt_kit: selectedKit };
        if (kit.note && S.noteKey) properties[S.noteKey] = kit.note;
        await cartRequest('cart/add.js', { method: 'POST', body: JSON.stringify({ items: [{ id: kit.id, quantity: 1, properties }] }) });
        const confirmed = await cartRequest('cart.js');
        const verified = verifyKit(confirmed, kit);
        cartCheckout.disabled = !verified;
        cartKit = verified ? selectedKit : null;
        stickyHook();
        setStatus(verified ? '' : (S.verifyFailed || 'We could not confirm your kit price. Please refresh the page and try again.'), !verified);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: confirmed } }));
      } catch (error) {
        cartCheckout.disabled = true;
        cartKit = null;
        stickyHook();
        setStatus(S.cartError || error.message || 'The cart could not be updated. Please try again.', true);
      } finally {
        setBusy(false);
      }
    };

    cartCloseButtons.forEach((button) => button.addEventListener('click', closeDrawer));
    if (upgradeButton) upgradeButton.addEventListener('click', () => replaceKit('complete'));
    cartAtc.addEventListener('click', () => {
      renderDrawer();
      openDrawer();
      replaceKit(selectedKit);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !cartDrawer.hidden) closeDrawer();
    });
    // A kit already in the cart (a returning visitor) puts the sticky bar straight into checkout mode.
    if (data.connected) {
      cartRequest('cart.js').then((cart) => {
        const kitName = ['basic', 'complete'].find((name) => verifyKit(cart, (data.kits || {})[name]));
        if (kitName) { cartKit = kitName; cartCheckout.disabled = false; stickyHook(); }
      }).catch(() => {});
    }
    syncKits();
  };

  const boot = () => document.querySelectorAll('[data-wt-iw]:not([data-wt-ready])').forEach((root) => {
    root.setAttribute('data-wt-ready', '');
    init(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
